<script lang="ts" setup>
import { computed, reactive, watch, ref, onBeforeMount } from 'vue';
import { VDataTable } from 'vuetify/components';
import { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import PermissionLock from '../../PermissionLock.vue';
import RoleSelect from '../../RoleSelect.vue';
import { useLocale } from '../../../composables/useLocale';
import { Role } from '../../../types/user';
import { useOrganizationsTableChecks } from './useOrganizationsTableChecks';
import { RoleOption, useUserRoles } from '../../../composables/useUserRoles';

export type AssociationData = {
  schoolUid: string;
  role: Role | null;
};

type Props = {
  search: string | undefined;
  user: CustomerMgmtTypes.GetUserResponse;
  loading?: boolean;
};

type Item = {
  uid: string;
  name: string;
  role: Role;
};

const { t } = useLocale();
const props = defineProps<Props>();
const model = defineModel<Map<string, AssociationData>>();

const { getRoleOptionsToUpdateTo, getRoleOptionsToCreateWith } = useUserRoles();

const roleOptionsForItems = reactive<Record<string, RoleOption[]>>({});

const isSelected = ({ uid }: Pick<Item, 'uid'>): boolean => model.value?.has(uid) ?? false;

/** Whether the association already exists server-side, as opposed to merely being ticked here. */
const hasStoredAssociation = ({ uid }: Pick<Item, 'uid'>): boolean =>
  props.user.associations.some((association) => association.schoolUnit.uid === uid);

const { callerOrganizations, itemsForbiddenToCreate, itemsForbiddenToEdit, itemsForbiddenToDelete } =
  useOrganizationsTableChecks(props.user, isSelected);

const getDefaultRole = (uid: string): Role | null => {
  const allUniqueRolesUserHas = [
    ...new Set(props.user.associations.map((a) => (a.isAdministrator ? 'admin' : a.role))),
  ];
  const defaultBase = allUniqueRolesUserHas.length === 1 ? allUniqueRolesUserHas[0] : null; // if user has several roles, force the creator to choose.
  const isAllowed = (role: Role) => roleOptionsForItems[uid]?.some((o) => o.type === role && o.allowed) ?? false;

  if (defaultBase === 'admin' && isAllowed('employee')) return 'employee';
  if (defaultBase !== null && isAllowed(defaultBase)) return defaultBase;
  // A default the caller may not pick here — a student ticked into a preschool, say — would pass
  // the save guard and only fail at the backend, so leave it unset and make them choose.
  return null;
};

watch(
  [callerOrganizations, props.user],
  async () => {
    const promises = callerOrganizations.value.map(async (org) => {
      // Ticking a row does not create the association, so a row the user is not a member of yet
      // is still governed by the create rules. Both checks are per row — the organization
      // selected in the app says nothing about what is allowed in a different one.
      const options = hasStoredAssociation(org)
        ? await getRoleOptionsToUpdateTo(props.user, org)
        : await getRoleOptionsToCreateWith(org);
      return { uid: org.uid, options };
    });

    const results = await Promise.all(promises);
    results.forEach(({ uid, options }) => {
      roleOptionsForItems[uid] = options;
    });
  },
  { immediate: true },
);

const initialSortedUids = ref<string[]>([]);

onBeforeMount(() => {
  initialSortedUids.value = [...callerOrganizations.value]
    .sort((a, b) => Number(isSelected(b)) - Number(isSelected(a)))
    .map((org) => org.uid);
});

const items = computed<Item[]>(() => {
  const orgMap = new Map(callerOrganizations.value.map((org) => [org.uid, org]));

  return initialSortedUids.value
    .map((uid) => {
      const org = orgMap.get(uid);
      if (!org) return null;

      const modelItem = model.value?.get(uid);
      return {
        uid: org.uid,
        name: org.name,
        role: modelItem ? modelItem.role : undefined,
      };
    })
    .filter((item): item is Item => item !== null);
});

const selectedItems = computed({
  get: () => items.value.filter((item) => isSelected(item)),
  set: (newSelection) => {
    const newModel = newSelection.map((item) => {
      const existingRole = model.value?.get(item.uid)?.role;
      const finalRole = existingRole || getDefaultRole(item.uid);
      return { schoolUid: item.uid, role: finalRole };
    });
    model.value = new Map(newModel.map((item) => [item.schoolUid, item]));
  },
});

const updateItemRole = (uid: string, newRole: Role) => {
  if (model.value?.has(uid)) {
    model.value = new Map<string, AssociationData>([
      ...model.value,
      [uid, { schoolUid: uid, role: newRole } satisfies AssociationData],
    ]);
  }
};

const allSelectedItems = computed(
  () => selectedItems.value.length === items.value.length - itemsForbiddenToCreate.value.size,
);
const noneSelectedItems = computed(() => selectedItems.value.length === itemsForbiddenToDelete.value.size);
const someSelectedItems = computed(() => !allSelectedItems.value && !noneSelectedItems.value);

const toggleSelectAll = (value: boolean) => {
  selectedItems.value = items.value.filter((item) =>
    value ? !itemsForbiddenToCreate.value.has(item.uid) : itemsForbiddenToDelete.value.has(item.uid),
  );
};

// Named after the row it selects: on its own the checkbox is one of a column of identical boxes.
const selectItemLabel = (item: Item) => `${t('lcp.select')}: ${item.name}`;

const headers = computed((): VDataTable['$props']['headers'] => [
  { key: 'data-table-select', sortable: false, align: 'center' },
  { title: t('lcp.organization_name'), key: 'name', align: 'start', sortable: true, nowrap: true },
  {
    title: t('lcp.role'),
    key: 'role',
    align: 'start',
    sortable: false,
    nowrap: true,
    headerProps: { class: 'pl-8' },
    minWidth: `${
      Math.max(
        t('lcp.role_admin').length,
        t('lcp.role_employee').length,
        t('lcp.role_student').length,
        t('lcp.role_guardian').length,
      ) * 1.25
    }ch`,
  },
]);
</script>
<template>
  <v-data-table
    v-model="selectedItems"
    :items="items"
    item-value="uid"
    :headers="headers"
    :items-per-page="100"
    :header-props="{ class: 'text-capitalize' }"
    :search="search"
    :multi-sort="false"
    :hover="true"
    fixed-header
    hide-default-footer
    show-select
    return-object
    :disabled="loading"
  >
    <template #header.data-table-select>
      <v-checkbox-btn
        :model-value="allSelectedItems"
        :indeterminate="someSelectedItems"
        :aria-label="t('lcp.select_all')"
        @update:model-value="toggleSelectAll"
        :disabled="loading"
      />
    </template>

    <template #item.data-table-select="{ item, internalItem, isSelected, toggleSelect }">
      <PermissionLock
        v-if="isSelected(internalItem) && itemsForbiddenToDelete.has(item.uid)"
        :reason="itemsForbiddenToDelete.get(item.uid)"
      />
      <PermissionLock
        v-else-if="!isSelected(internalItem) && itemsForbiddenToCreate.has(item.uid)"
        :reason="itemsForbiddenToCreate.get(item.uid)"
        icon="mdi-lock-outline"
      />
      <v-checkbox-btn
        v-else
        :model-value="isSelected(internalItem)"
        :disabled="loading"
        :aria-label="selectItemLabel(item)"
        @update:model-value="toggleSelect(internalItem)"
      />
    </template>

    <template #item.role="{ item, internalItem, isSelected }">
      <div v-if="isSelected(internalItem)" class="d-flex align-center">
        <RoleSelect
          :model-value="item.role"
          @update:model-value="(val) => updateItemRole(item.uid, val!)"
          :options="roleOptionsForItems[item.uid] ?? []"
          :disabled="loading || itemsForbiddenToEdit.has(item.uid)"
          hide-details
          density="compact"
        />
        <PermissionLock v-if="itemsForbiddenToEdit.has(item.uid)" :reason="itemsForbiddenToEdit.get(item.uid)" />
        <v-icon v-else style="visibility: hidden"></v-icon>
      </div>
    </template>
  </v-data-table>
</template>
